import { useState, useCallback, useEffect, useRef, useId } from 'react';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { alertMessages } from '@/constants';
import { SERVER_FAILURE_MESSAGE } from '@/constants/alertMessages';
import { cancelOrigin, isCircuitBreakerBlock } from '@/api/failureClass';
import { useFeedbackStore } from '@/store/useFeedbackStore';
import { extractApiMessage, isAxiosErrorWithResponse } from '@/lib/apiError';

/**
 * The copy for a failed action when the caller passed no `errorMessage`
 * (#1185). An Axios error with a response shows what the server said (its
 * `detail`, or the one server-failure sentence for a 5xx), never axios's own
 * "Request failed with status code 500". Any other error keeps its message.
 */
function actionErrorMessage(err: unknown, errorObj: Error): string {
  if (isAxiosErrorWithResponse(err)) return extractApiMessage(err, alertMessages.generic.error);
  return errorObj.message || alertMessages.generic.error;
}

/**
 * Options for executing an async action
 */
export interface AsyncActionOptions {
  /** Message shown on success */
  successMessage?: string;
  /** Message shown on error (overrides default) */
  errorMessage?: string;
  /** Whether to show error toast (default: true) */
  showError?: boolean;
  /** Whether to show success toast (default: true if successMessage provided) */
  showSuccess?: boolean;
  /** Callback to run on success */
  onSuccess?: () => void;
  /** Callback to run on error */
  onError?: (error: Error) => void;
}

/**
 * Return type for useAsyncAction hook
 */
export interface AsyncActionResult<T> {
  /** Whether action is in progress */
  loading: boolean;
  /** Error from last execution, if any */
  error: Error | null;
  /** Execute the async action */
  execute: (
    action: () => Promise<T>,
    options?: AsyncActionOptions
  ) => Promise<T | undefined>;
  /** Reset loading and error state */
  reset: () => void;
}

/**
 * Hook for consistent async action handling with loading states and alerts.
 *
 * Cancel rules (#1628-c1, PM ruling 11-02), by `cancelOrigin`: an action we or
 * the person stopped (our abort, an aborted signal, a hidden or closing tab,
 * including status 0 while hidden) pushes nothing, clears nothing and does not
 * call `onError`. One the browser killed while the page is visible shows
 * `SERVER_FAILURE_MESSAGE` under the action's source (a later success clears
 * it) and does not call `onError`, whatever `errorMessage` / `showError` say.
 * A request blocked by the circuit breaker (PM 11-02 04:59Z) is shown the same
 * way, never as the raw "Circuit breaker is open".
 *
 * This hook standardizes the pattern of:
 * 1. Setting loading state
 * 2. Executing an async action
 * 3. Showing success/error alerts
 * 4. Resetting loading state
 *
 * @example
 * // Basic usage
 * const { loading, execute } = useAsyncAction();
 *
 * const handleDelete = async () => {
 *   await execute(
 *     () => deleteContact(id),
 *     { successMessage: 'Contact deleted successfully' }
 *   );
 *   onClose();
 * };
 *
 * <Button disabled={loading} onClick={handleDelete}>
 *   {loading ? 'Deleting...' : 'Delete'}
 * </Button>
 *
 * @example
 * // With error handling
 * const { loading, error, execute } = useAsyncAction<Contact>();
 *
 * const handleSave = async () => {
 *   const result = await execute(
 *     () => saveContact(data),
 *     {
 *       successMessage: 'Contact saved',
 *       onSuccess: () => navigate('/contacts'),
 *       onError: (err) => zone?.push('Save failed: ' + err.message, 'error'),
 *     }
 *   );
 *   if (result) {
 *     // saved successfully — `result` is the Contact returned from saveContact
 *   }
 * };
 *
 * @example
 * // Silent execution (no alerts)
 * const { loading, execute } = useAsyncAction();
 *
 * const handleRefresh = async () => {
 *   await execute(
 *     () => fetchData(),
 *     { showError: false, showSuccess: false }
 *   );
 * };
 */
export function useAsyncAction<T = void>(): AsyncActionResult<T> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const zone = useFeedbackZone();
  // One hook instance is one action (#1628): its failure is cleared when it
  // next succeeds, and replaced when it next fails.
  const key = useId();

  // F8: skip setState and user callbacks once the host has unmounted, so
  // a slow async action resolving after teardown doesn't run callbacks
  // against a gone tree or trigger React unmount warnings.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const execute = useCallback(
    async (
      action: () => Promise<T>,
      options: AsyncActionOptions = {}
    ): Promise<T | undefined> => {
      const {
        successMessage,
        errorMessage,
        showError: shouldShowError = true,
        showSuccess: shouldShowSuccess = !!successMessage,
        onSuccess,
        onError,
      } = options;

      setLoading(true);
      setError(null);

      try {
        const result = await action();
        if (!isMountedRef.current) return undefined;

        zone?.clearSource(key);
        if (shouldShowSuccess && successMessage) {
          zone?.push(successMessage, 'success', { source: key });
        }

        onSuccess?.();
        return result;
      } catch (err) {
        if (!isMountedRef.current) return undefined;
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);

        // A cancel-class end (#1628-c1): ours shows nothing and leaves the
        // failure already shown; the browser's kill is a failed action.
        const origin = cancelOrigin(err);
        if (origin === 'ours') return undefined;
        if (origin === 'browser' || isCircuitBreakerBlock(err)) {
          zone?.push(SERVER_FAILURE_MESSAGE, 'error', { source: key });
          return undefined;
        }

        if (shouldShowError) {
          const message = errorMessage || actionErrorMessage(err, errorObj);
          zone?.push(message, 'error', { source: key });
        }

        onError?.(errorObj);
        return undefined;
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    },
    [zone, key]
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return { loading, error, execute, reset };
}

/**
 * Return type for useAsyncActions hook
 */
export interface UseAsyncActionsReturn {
  /** Execute an action with a unique key */
  execute: <T>(
    key: string,
    action: () => Promise<T>,
    options?: AsyncActionOptions
  ) => Promise<T | undefined>;
  /** Check if a specific action is loading */
  isLoading: (key: string) => boolean;
  /** Get error for a specific action */
  getError: (key: string) => Error | null;
  /** Reset loading/error state (all or by key) */
  reset: (key?: string) => void;
}

/**
 * Hook for managing multiple concurrent async actions
 *
 * @example
 * const actions = useAsyncActions();
 *
 * // Each action is tracked separately
 * await actions.execute('save', () => saveData(), { successMessage: 'Saved' });
 * await actions.execute('delete', () => deleteData(), { successMessage: 'Deleted' });
 *
 * // Check loading state for specific action
 * const isSaving = actions.isLoading('save');
 * const isDeleting = actions.isLoading('delete');
 */
export function useAsyncActions(): UseAsyncActionsReturn {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, Error | null>>({});
  const zone = useFeedbackZone();
  const hookId = useId();

  // Mirror the F8 unmount guard from useAsyncAction so resolutions after
  // the host has unmounted don't fire setState / callbacks against a
  // gone tree (would surface as React "state update on unmounted
  // component" warnings).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const execute = useCallback(
    async <T>(
      key: string,
      action: () => Promise<T>,
      options: AsyncActionOptions = {}
    ): Promise<T | undefined> => {
      const {
        successMessage,
        errorMessage,
        showError: shouldShowError = true,
        showSuccess: shouldShowSuccess = !!successMessage,
        onSuccess,
        onError,
      } = options;

      const source = `${hookId}:${key}`;
      setLoadingStates((prev) => ({ ...prev, [key]: true }));
      setErrors((prev) => ({ ...prev, [key]: null }));

      try {
        const result = await action();
        if (!isMountedRef.current) return undefined;

        // Success clears this action's earlier failure (#1628), wherever it
        // was shown; keys are unique per hook instance and action key.
        zone?.clearSource(source);
        useFeedbackStore.getState().clearSource(source);
        if (shouldShowSuccess && successMessage) {
          zone?.push(successMessage, 'success', { source });
        }

        onSuccess?.();
        return result;
      } catch (err) {
        if (!isMountedRef.current) return undefined;
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setErrors((prev) => ({ ...prev, [key]: errorObj }));

        const origin = cancelOrigin(err);
        if (origin === 'ours') return undefined;
        if (origin === 'browser' || isCircuitBreakerBlock(err)) {
          zone?.push(SERVER_FAILURE_MESSAGE, 'error', { source });
          return undefined;
        }

        if (shouldShowError) {
          const message = errorMessage || actionErrorMessage(err, errorObj);
          zone?.push(message, 'error', { source });
        }

        onError?.(errorObj);
        return undefined;
      } finally {
        if (isMountedRef.current) {
          setLoadingStates((prev) => ({ ...prev, [key]: false }));
        }
      }
    },
    [zone, hookId]
  );

  const isLoading = useCallback(
    (key: string) => loadingStates[key] ?? false,
    [loadingStates]
  );

  const getError = useCallback(
    (key: string) => errors[key] ?? null,
    [errors]
  );

  const reset = useCallback((key?: string) => {
    if (key) {
      setLoadingStates((prev) => ({ ...prev, [key]: false }));
      setErrors((prev) => ({ ...prev, [key]: null }));
    } else {
      setLoadingStates({});
      setErrors({});
    }
  }, []);

  return { execute, isLoading, getError, reset };
}
